import React, { useEffect, useState, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/effect-fade';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const TARGET_DATE = 'January 14, 2027 09:00:00';

const HERO_SLIDES = [
  '/hero-road.webp',
  '/hero-road-1.webp',
  '/hero-road-2.webp',
  '/hero-road-3.webp'
];

const TRACKS = [
  { 
    id: "01",
    title: "Road Safety & Vision Zero", 
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    items: [
      "Safe System Approach", "Forgiving Roads and Roadsides", "School Zone Safety", 
      "Vulnerable Road User Protection", "Road Safety Audits", "AI-enabled Safety Solutions"
    ]
  },
  { 
    id: "02",
    title: "Smart Infrastructure & Digital Transformation", 
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
      </svg>
    ),
    items: [
      "Intelligent Transport Systems (ITS)", "Artificial Intelligence and Data Analytics", 
      "Digital Twins for Highway Management", "Connected Mobility Solutions", "Smart Traffic Management"
    ]
  },
  { 
    id: "03",
    title: "Sustainable & Green Roads", 
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    items: [
      "Low-Carbon Road Infrastructure", "Bio-Bitumen and Alternative Materials", 
      "Circular Economy Practices", "Green Highway Development", "Net-Zero Infrastructure Strategies"
    ]
  },
  { 
    id: "04",
    title: "Asset Management & Maintenance", 
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    items: [
      "Pavement Management Systems", "Predictive Maintenance Technologies", 
      "Performance-Based Contracts", "Infrastructure Monitoring Solutions"
    ]
  },
  { 
    id: "05",
    title: "Climate Resilience", 
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    ),
    items: [
      "Climate-Adaptive Infrastructure", "Flood and Heat Resilience", 
      "Disaster Risk Reduction", "Resilient Transport Networks"
    ]
  },
  { 
    id: "06",
    title: "Future Mobility", 
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    items: [
      "EV Infrastructure", "Multimodal Transport Integration", 
      "Logistics and Freight Corridors", "Emerging Mobility Technologies"
    ]
  }
];

const PARTICIPANTS = [
  {
    title: "GOVERNMENT",
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
      </svg>
    ),
    items: [
      "Ministry of Road Transport & Highways (MoRTH)",
      "National Highway Agencies - NHAI & NHIDCL",
      "State Public Works Departments (PWDs)",
      "Urban Development Authorities",
      "Smart City Mission Authorities",
      "Traffic Police and Road Safety Departments"
    ]
  },
  {
    title: "INDUSTRY",
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
    items: [
      "Infrastructure Developers",
      "EPC Contractors",
      "Engineering Consultants",
      "Construction Equipment Manufacturers",
      "Material Suppliers",
      "ITS and Smart Mobility Solution Providers",
      "Technology and Innovation Companies"
    ]
  },
  {
    title: "RESEARCH & ACADEMIA",
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
      </svg>
    ),
    items: [
      "Universities and Academic Institutions",
      "Research Organizations",
      "Technical Experts and Subject Specialists",
      "Transportation and Infrastructure Researchers"
    ]
  },
  {
    title: "INTERNATIONAL ORGANIZATIONS",
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    items: [
      "Development Finance Institutions and Banks",
      "Multilateral Agencies",
      "Global Infrastructure Associations",
      "International Road and Transportation Organizations"
    ]
  }
];

const STATS = [
  { metric: 500, suffix: "+", label: "Delegates", icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /> },
  { metric: 60, suffix: "+", label: "National & International Speakers", icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /> },
  { metric: 60, suffix: "+", label: "Exhibitors", icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /> },
  { metric: 20, suffix: "+", label: "Participating Countries", icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /> },
  { metric: 8, suffix: "+", label: "Knowledge Sessions", icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /> }
];

// Calculate time immediately to prevent initial 00 load flash
const getInitialTime = () => {
  const targetDate = new Date(TARGET_DATE).getTime();
  const difference = targetDate - new Date().getTime();
  if (difference > 0) {
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000)
    };
  }
  return { days: 0, hours: 0, minutes: 0, seconds: 0 };
};

function FloatingInfoStrip({ timeLeft }) {
  return (
    <div className="bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] border border-slate-100 rounded-2xl md:rounded-[2rem] p-4 md:p-6 w-[92%] md:w-[95%] xl:w-full max-w-7xl mx-auto flex flex-col xl:flex-row items-center justify-between gap-6 relative z-30 transform hover:-translate-y-1 transition-transform duration-500">
      <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6 xl:gap-8 text-center md:text-left w-full xl:w-auto justify-center xl:justify-start">
        <img 
          src="/gris-2026.png" 
          alt="GRIS 2027 Logo" 
          width={300} 
          height={96} 
          loading="lazy"
          decoding="async"
          className="h-16 md:h-20 lg:h-24 w-auto object-contain" 
        />
        <div className="hidden md:block w-px h-16 bg-slate-200"></div>
        <div className="flex flex-col justify-center">
          <div className="text-2xl md:text-3xl lg:text-4xl font-black text-[#0a1d37] tracking-tight leading-none mb-1.5 md:mb-2">14–15 JAN 2027</div>
          <div className="flex items-start justify-center md:justify-start gap-1.5 text-[10px] md:text-[11px] font-bold text-slate-800 uppercase tracking-widest">
            {/* ACCESSIBILITY FIX: Contrast increased to orange-700 */}
            <svg className="w-3.5 h-3.5 text-orange-700 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <div className="flex flex-col text-center md:text-left">
              <span>Dr.Ambedkar International Centre</span>
              <span className="text-orange-700 font-black text-[9px] md:text-[10px] mt-0.5 tracking-wider">New Delhi, India</span>
            </div>
          </div>
        </div>
      </div>
      <div className="hidden xl:block w-px h-16 bg-slate-200"></div>
      
      {/* ACCESSIBILITY FIX: Added role="timer" to div with aria-label to clear prohibited ARIA error */}
      <div className="flex items-center justify-center gap-2.5 md:gap-4 w-full xl:w-auto flex-nowrap" role="timer" aria-label="Countdown timer to event">
        {[
          { label: "Days", value: timeLeft.days },
          { label: "Hours", value: timeLeft.hours },
          { label: "Mins", value: timeLeft.minutes },
          { label: "Secs", value: timeLeft.seconds, isAccent: true }
        ].map((block, i) => (
          <div key={i} className="flex flex-col items-center bg-slate-50 px-3 md:px-5 py-2 md:py-3 rounded-xl border border-slate-100 min-w-[60px] md:min-w-[80px] shadow-sm">
            <div className={`text-xl md:text-3xl font-black ${block.isAccent ? 'text-orange-700' : 'text-[#0a1d37]'}`}>{String(block.value).padStart(2, '0')}</div>
            <div className="text-[8px] md:text-[10px] uppercase font-black text-slate-800 mt-0.5 tracking-widest">{block.label}</div>
          </div>
        ))}
      </div>
      <div className="hidden xl:block w-px h-16 bg-slate-200"></div>
      <div className="w-full xl:w-auto flex justify-center mt-2 xl:mt-0">
        {/* ACCESSIBILITY FIX: Switched text-white to text-[#0a1d37] against bg-orange-500 for WCAG contrast passing */}
        <Link to="/delegates" aria-label="Secure Pass for Delegates Registration" className="w-full md:w-auto px-8 py-3.5 md:py-4 bg-orange-500 hover:bg-orange-600 text-[#0a1d37] text-sm font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 active:scale-95 text-center whitespace-nowrap">Secure Pass</Link>
      </div>
    </div>
  );
}

export default function HomePage() {
  const pageRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(getInitialTime);

  useEffect(() => {
    const calculateTime = () => setTimeLeft(getInitialTime());
    const interval = setInterval(calculateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.animate-fade-up').forEach((element) => {
        gsap.fromTo(element,
          { opacity: 0, y: 40 },
          { opacity: 1, y: 0, duration: 1, ease: 'power3.out', scrollTrigger: { trigger: element, start: 'top 85%' } }
        );
      });
      gsap.utils.toArray('.stat-counter').forEach((counter) => {
        const target = parseInt(counter.getAttribute('data-target'), 10);
        gsap.fromTo(counter,
          { textContent: '0' },
          { textContent: target, duration: 2.5, ease: 'power2.out', snap: { textContent: 1 }, scrollTrigger: { trigger: counter, start: 'top 90%' } }
        );
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef} className="relative bg-slate-50 text-slate-800 font-sans selection:bg-orange-500 selection:text-white pb-20 w-full max-w-[100vw] overflow-x-hidden">
      
      {/* SEO FIX: Exact GRIS Title placement */}
      <title>GRIS 2027 | Global Road Infratech Summit & Expo | IRF India</title>
      
      {/* SEO FIX: GRIS Lead Meta Description */}
      <meta name="description" content="GRIS 2027 – Global Road Infratech Summit & Expo, 14–15 January 2027 at Dr. Ambedkar International Centre, New Delhi, organised by IRF India Chapter." />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href="https://gris.indiairf.com/" />

      <meta property="og:title" content="GRIS 2027 | Global Road Infratech Summit & Expo | IRF India" />
      <meta property="og:description" content="GRIS 2027 – Global Road Infratech Summit & Expo, 14–15 January 2027 at Dr. Ambedkar International Centre, New Delhi, organised by IRF India Chapter." />
      <meta property="og:image" content="/hero-road.webp" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />

      {/* 🚀 CRITICAL LCP PRELOADS & RESPONSIVE BACKGROUND STYLING */}
      <link rel="preload" fetchpriority="high" as="image" href="/hero-road.webp" type="image/webp" />

      <style dangerouslySetInnerHTML={{__html: `
        .ultra-lcp-hero { background-image: url('/hero-road-mobile.webp'); }
        @media (min-width: 768px) { .ultra-lcp-hero { background-image: url('/hero-road.webp'); } }
        .will-change-transform { will-change: transform, opacity; }
      `}} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Event",
                "@id": "https://gris.indiairf.com/#event",
                "name": "Global Road Infratech Summit & Expo 2027",
                "alternateName": [
                  "GRIS 2027",
                  "GRIS",
                  "Global Road Infratech Summit 2027",
                  "Global Road InfraTech Summit & Expo 2027"
                ],
                "description": "Global Road Infratech Summit & Expo 2027 is a road infrastructure summit and exhibition focused on safer, smarter and sustainable roads, bringing together government, industry, research, academia and international infrastructure organizations.",
                "startDate": "2027-01-14T09:00:00+05:30",
                "endDate": "2027-01-15T18:00:00+05:30",
                "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
                "eventStatus": "https://schema.org/EventScheduled",
                "url": "https://gris.indiairf.com/",
                "image": [
                  "https://gris.indiairf.com/hero-road.webp"
                ],
                "location": {
                  "@type": "Place",
                  "name": "Dr.Ambedkar International Centre (DAIC)",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "15, Janpath District, Windsor Place",
                    "addressLocality": "New Delhi",
                    "addressRegion": "Delhi",
                    "postalCode": "110001",
                    "addressCountry": "IN"
                  }
                },
                "organizer": {
                  "@id": "https://gris.indiairf.com/#organization"
                },
                "offers": {
                  "@type": "Offer",
                  "url": "https://gris.indiairf.com/delegates",
                  "priceCurrency": "INR",
                  "availability": "https://schema.org/InStock",
                  "validFrom": "2026-01-01"
                }
              },
              {
                "@type": "Organization",
                "@id": "https://gris.indiairf.com/#organization",
                "name": "International Road Federation (IRF) - India Chapter",
                "url": "https://indiairf.com"
              },
              {
                "@type": "WebSite",
                "@id": "https://gris.indiairf.com/#website",
                "url": "https://gris.indiairf.com/",
                "name": "GRIS 2027 | Global Road Infratech Summit & Expo",
                "publisher": {
                  "@id": "https://gris.indiairf.com/#organization"
                },
                "inLanguage": "en-IN"
              },
              {
                "@type": "WebPage",
                "@id": "https://gris.indiairf.com/#webpage",
                "url": "https://gris.indiairf.com/",
                "name": "GRIS 2027 | Global Road Infratech Summit & Expo | IRF India",
                "description": "GRIS 2027 – Global Road Infratech Summit & Expo, 14–15 January 2027 at Dr. Ambedkar International Centre, New Delhi, organised by IRF India Chapter.",
                "isPartOf": {
                  "@id": "https://gris.indiairf.com/#website"
                },
                "about": {
                  "@id": "https://gris.indiairf.com/#event"
                },
                "inLanguage": "en-IN"
              },
              {
                "@type": "FAQPage",
                "@id": "https://gris.indiairf.com/#faq",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "What is GRIS 2027?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "GRIS 2027 is the Global Road Infratech Summit & Expo 2027, a road infrastructure summit and exhibition focused on safer, smarter and sustainable roads."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "When is GRIS 2027?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "GRIS 2027 will be held on 14–15 January 2027."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Where is GRIS 2027 being held?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "GRIS 2027 will be held at Dr. Ambedkar International Centre, New Delhi, India."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Who should participate in GRIS 2027?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "GRIS 2027 is intended for government organizations, infrastructure developers, EPC contractors, engineering consultants, technology companies, research and academic institutions, international organizations and other road infrastructure professionals."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "How can I register for GRIS 2027?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Delegate registration is available through the GRIS 2027 delegate registration page."
                    }
                  }
                ]
              }
            ]
          })
        }}
      />

      {/* 1. CINEMATIC HERO SECTION */}
      <section className="relative flex flex-col items-center w-full max-w-[100vw]" aria-label="Introduction Banner Area">
        {/* FIX: min-h constraint add kiya gaya taaki 100% zoom par hero badge chhipe nahi */}
        <div className="relative w-full min-h-[680px] lg:min-h-[800px] h-[65vh] md:h-[75vh] lg:h-[85vh] overflow-hidden bg-[#0a1d37] bg-cover bg-top ultra-lcp-hero">
          <Swiper
            modules={[Autoplay, EffectFade]}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            speed={1500}
            loop={true}
            watchSlidesProgress={true}
            allowTouchMove={false}
            className="w-full h-full"
            role="region" 
            aria-label="Hero Image Carousel"
          >
            {HERO_SLIDES.map((src, index) => (
              <SwiperSlide 
                key={index} 
                className="w-full h-full bg-[#0a1d37] overflow-hidden relative" 
                role="group"
                aria-label={`Slideshow image showcase frame ${index + 1}`}
              >
                <div 
                  className="w-full h-full bg-cover bg-top opacity-50 absolute inset-0" 
                  style={{ backgroundImage: `url(${src})`, transformOrigin: 'top center' }} 
                />
              </SwiperSlide>
            ))}
          </Swiper>
          
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a1d37]/90 via-[#0a1d37]/40 to-transparent z-10" />
          
          {/* FIX: Inner wrapper ki padding adjust ki gayi hai content overlap ko rokne ke liye */}
          <div className="absolute inset-0 z-20 flex flex-col justify-center w-full max-w-7xl mx-auto px-6 lg:px-8 text-center lg:text-left pt-28 lg:pt-32 pb-32 md:pb-36 lg:pb-44">
            <div className="animate-fade-up will-change-transform max-w-4xl">
              <span className="inline-block px-5 py-2.5 bg-[#0a1d37]/95 border-2 border-orange-500 text-white rounded-full text-[11px] md:text-xs font-black uppercase tracking-widest mb-6 shadow-2xl backdrop-blur-md">
                Flagship Event of IRF India Chapter
              </span>
              <h1 className="text-4xl md:text-6xl lg:text-[5rem] font-black tracking-tight leading-[1.05] text-white mb-6 drop-shadow-lg">
                Global Road Infratech <br className="hidden md:block"/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-[#FFB067]">Summit & Expo 2027</span>
              </h1>
              <p className="text-base md:text-xl lg:text-2xl text-slate-300 leading-relaxed font-medium max-w-2xl mx-auto lg:mx-0 mb-4">
                From Vision to Impact: Transforming Road Infrastructure for a Safer, Smarter and Sustainable India.
              </p>
              {/* SEO FIX: Visible, semantic contextual relationship string placed perfectly inside the Hero */}
              <p className="text-sm md:text-base text-slate-400 font-medium max-w-2xl mx-auto lg:mx-0">
                <strong className="text-white">GRIS 2027</strong> (Global Road Infratech Summit & Expo 2027) is the flagship road infrastructure summit and exhibition of the International Road Federation – India Chapter.
              </p>
            </div>
          </div>
        </div>

        <div className="w-full relative z-30 -mt-20 md:-mt-24 lg:absolute lg:bottom-0 lg:translate-y-1/2 px-0">
          <FloatingInfoStrip timeLeft={timeLeft} />
        </div>
      </section>

      {/* AI / SEARCH-FRIENDLY EVENT SUMMARY — visual design intentionally unchanged */}
      <section className="sr-only" aria-label="About GRIS 2027">
        <h2>About Global Road Infratech Summit & Expo 2027</h2>
        <p>
          Global Road Infratech Summit & Expo 2027 (GRIS 2027) will be held on 14–15 January 2027
          at Dr. Ambedkar International Centre, New Delhi, India. The summit brings together
          government, industry, research, academia and international organizations working across
          road infrastructure and mobility.
        </p>
        <p>
          GRIS 2027 focuses on road safety and Vision Zero, smart infrastructure and digital
          transformation, sustainable and green roads, asset management and maintenance, climate
          resilience, and future mobility.
        </p>
        <p>
          Expected participation includes 500+ delegates, 60+ national and international speakers,
          60+ exhibitors, participants from 20+ countries, and 8+ knowledge sessions.
        </p>
      </section>

     {/* 2.5 ANIMATED TICKER */}
      <div className="w-full max-w-[100vw] bg-[#0a1d37] text-white py-3 overflow-hidden relative flex items-center border-b border-orange-500/30 mt-12 md:mt-16 lg:mt-32">
         {/* ACCESSIBILITY FIX: text-white -> text-[#0a1d37] for contrast on orange */}
         <div className="bg-orange-500 text-[#0a1d37] font-black text-[10px] md:text-sm px-4 md:px-6 py-3.5 absolute left-0 z-10 flex items-center h-full shadow-[5px_0_15px_rgba(0,0,0,0.5)]">
           <span className="uppercase tracking-widest whitespace-nowrap">Recent Updates</span>
         </div>
         <div className="flex whitespace-nowrap pl-[130px] md:pl-[200px]">
          <div className="animate-ticker inline-flex items-center gap-10 text-xs md:text-sm font-medium tracking-wide">
       <span>⭐ MSRDC joins GRIS 2027 as Co-Host Partner</span>
       <span>⭐ IRB Infrastructure Developers onboarded as Platinum Sponsor</span>
       <span>⭐ Navayuga Engineering Company Ltd. joins as Platinum Partner</span>
       <span>⭐ Montecarlo Limited joins as Gold Partner</span>
       <span>⭐ DRA Infracon partners as Diamond Sponsor</span>
       <span>⭐ DCC Infra Projects Ltd. joins as Silver Partner</span>
       <span>⭐ Additional Sponsorship Opportunities Available</span>
     </div>
         </div>
         <style dangerouslySetInnerHTML={{__html: `
           @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
           .animate-ticker { animation: ticker 35s linear infinite; padding-right: 2.5rem; will-change: transform; }
           .animate-ticker:hover { animation-play-state: paused; }
         `}} />
      </div>

      {/* 3. LOGOS SECTION */}
      <section className="bg-slate-50 pt-16 pb-16 relative z-20 border-b border-slate-200" aria-label="Summit organizers and industrial network partners layout" style={{ contentVisibility: 'auto', containIntrinsicSize: '500px' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col items-center">
          <div className="mb-10 w-full flex flex-col items-center">
            <h2 className="text-center text-sm font-black text-slate-900 uppercase tracking-widest mb-6">Organised By</h2>
            <div className="flex flex-wrap justify-center items-center gap-10 md:gap-16">
              
              {/* IRF Geneva Logo Link */}
              <a 
                href="https://irfofficial.org/" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Visit IRF Geneva International Association Website"
                className="transition-transform duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-lg p-2"
              >
                <img 
                  src="/irf-geneva.png" 
                  alt="IRF Geneva International Association Logo" 
                  width={150} 
                  height={96} 
                  loading="lazy" 
                  decoding="async" 
                  className="h-16 md:h-24 w-auto object-contain" 
                />
              </a>

              {/* IRF India Logo Link */}
              <a 
                href="https://www.indiairf.com" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Visit IRF India Chapter Federation Website"
                className="transition-transform duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-lg p-2"
              >
                <img 
                  src="/irf-india.png" 
                  alt="IRF India Chapter Federation Logo" 
                  width={150} 
                  height={96} 
                  loading="lazy" 
                  decoding="async" 
                  className="h-16 md:h-24 w-auto object-contain" 
                />
              </a>

            </div>
          </div>

          <div className="w-full bg-white border border-slate-300 md:rounded-lg shadow-sm flex flex-col lg:flex-row items-stretch justify-center py-8 px-2 md:px-6 mx-auto mt-6">
            <div className="flex-1 flex flex-col items-center justify-start px-4">
              <h2 className="italic font-extrabold text-slate-800 text-[17px] mb-6">In Collaboration with</h2>
              <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                <div className="flex flex-col items-center">
                  <img src="/its-india.png" alt="ITS India Forum Partner Logo" width={100} height={100} loading="lazy" decoding="async" className="h-[80px] md:h-[100px] w-auto object-contain" />
                  <span className="text-[11px] font-bold mt-3 text-slate-900">ITS India Forum</span>
                </div>
                <div className="hidden md:block h-14 w-px bg-slate-400 mb-4"></div>
                <div className="flex flex-col items-center">
                  <img src="/bitumen-form.png" alt="Bitumen Forum Partner Logo" width={100} height={100} loading="lazy" decoding="async" className="h-[80px] md:h-[100px] w-auto object-contain" />
                  <span className="text-[11px] font-bold mt-3 text-slate-900">Bitumen Forum</span>
                </div>
              </div>
            </div>
            <div className="hidden lg:block w-px bg-slate-300 mx-2"></div>
            <div className="lg:hidden w-3/4 h-px bg-slate-200 my-8 mx-auto"></div>
            <div className="flex-1 flex flex-col items-center justify-start px-4">
              <h2 className="italic font-extrabold text-slate-800 text-[17px] mb-6">Event Strategy Partner</h2>
              <div className="flex flex-col items-center">
                <img src="/rex.png" alt="Rex Conferences Strategy Partner Logo" width={120} height={90} loading="lazy" decoding="async" className="h-[70px] md:h-[90px] w-auto object-contain" />
                <span className="text-[12px] font-medium mt-3 text-slate-800">Rex Conferences</span>
              </div>
            </div>
            <div className="hidden lg:block w-px bg-slate-300 mx-2"></div>
            <div className="lg:hidden w-3/4 h-px bg-slate-200 my-8 mx-auto"></div>
            <div className="flex-1 flex flex-col items-center justify-start px-4">
              <h2 className="italic font-extrabold text-slate-800 text-[17px] mb-6">Knowledge Partner</h2>
              <div className="flex flex-col items-center">
                <img src="/crri.jpg" alt="CSIR CRRI Knowledge Partner Logo" width={120} height={90} loading="lazy" decoding="async" className="h-[70px] md:h-[90px] w-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. STRATEGIC FOCUS AREAS */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-8 animate-fade-up will-change-transform" aria-label="Summit target architecture pillars" style={{ contentVisibility: 'auto', containIntrinsicSize: '800px' }}>
        <div className="text-center max-w-3xl mx-auto mb-16">
          {/* ACCESSIBILITY FIX: Contrast increased to orange-700 */}
          <span className="text-xs font-bold text-orange-700 uppercase tracking-widest">Summit Architecture</span>
          <h2 className="text-4xl md:text-5xl font-black text-[#0a1d37] tracking-tight mt-3 mb-6">Strategic Focus Areas</h2>
          {/* ACCESSIBILITY FIX: Contrast increased to slate-600 */}
          <p className="text-lg text-slate-600 leading-relaxed">Move beyond vision and aspiration towards transformation that delivers measurable and lasting impact through collective commitment.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {TRACKS.map((theme, idx) => (
            <div key={idx} className="bg-white p-8 rounded-[2rem] shadow-sm hover:shadow-2xl border border-slate-200 border-b-4 border-b-transparent hover:border-b-orange-500 transition-all duration-300 hover:-translate-y-2 flex flex-col group">
              <div className="flex items-center justify-between mb-6">
                <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center text-orange-700 group-hover:bg-orange-500 group-hover:text-white transition-colors duration-300">{theme.icon}</div>
                <span className="font-black text-4xl text-slate-100 group-hover:text-orange-100 transition-colors duration-300">{theme.id}</span>
              </div>
              <h3 className="text-xl lg:text-2xl font-black text-[#0a1d37] mb-6 leading-tight group-hover:text-orange-700 transition-colors duration-300">{theme.title}</h3>
              <ul className="space-y-3 mt-auto">
                {theme.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[14px] text-slate-600 font-medium leading-snug">
                    <span className="text-orange-700 mt-0.5 flex-shrink-0">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* 5. WHO SHOULD PARTICIPATE SECTION */}
      <section className="py-24 lg:py-32 bg-slate-50 border-t border-slate-200 animate-fade-up will-change-transform" aria-label="Summit audience and network registration layout profile" style={{ contentVisibility: 'auto', containIntrinsicSize: '800px' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            {/* ACCESSIBILITY FIX: Contrast increased to orange-700 */}
            <span className="text-xs font-bold text-orange-700 uppercase tracking-widest">Audience Profile</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#0a1d37] tracking-tight mt-3 mb-6">Who Should Participate?</h2>
            {/* ACCESSIBILITY FIX: Contrast increased to slate-600 */}
            <p className="text-lg text-slate-600 font-medium">Join Senior Government Policymakers, Industry Leaders, and Infrastructure Experts shaping the future of global mobility.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {PARTICIPANTS.map((group, idx) => (
              <div key={idx} className="bg-white p-8 lg:p-10 rounded-[2rem] shadow-sm hover:shadow-xl border border-slate-200 transition-all duration-300 hover:-translate-y-2 group flex flex-col">
                <div className="flex items-center gap-6 mb-8 border-b border-slate-100 pb-6">
                  <div className="w-20 h-20 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-700 group-hover:bg-orange-500 group-hover:text-white transition-colors duration-300 flex-shrink-0 shadow-inner">{group.icon}</div>
                  <h3 className="text-2xl font-black text-[#0a1d37] leading-tight">{group.title}</h3>
                </div>
                <ul className="space-y-4 mt-auto">
                  {group.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-600 font-medium">
                      <span className="text-orange-700 mt-1 flex-shrink-0">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </span>
                      <span className="leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. DYNAMIC COUNTER SECTION */}
      <section className="bg-[#0a1d37] py-24 relative overflow-hidden animate-fade-up will-change-transform" aria-label="summit participation statistics counters display area" style={{ contentVisibility: 'auto', containIntrinsicSize: '400px' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at center, #ffffff 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16"><span className="text-xs font-bold text-orange-400 uppercase tracking-widest">Summit Scale</span><h2 className="text-3xl md:text-5xl font-black text-white mt-3">Expected Participation</h2></div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
            {STATS.map((stat, i) => (
              <div key={i} className={`bg-white/5 backdrop-blur-sm border border-white/10 p-6 rounded-3xl flex flex-col items-center justify-center text-center transform hover:-translate-y-2 transition-transform duration-300 hover:bg-white/10 hover:border-orange-500/50 ${i === 4 ? 'col-span-2 lg:col-span-1' : ''}`}>
                <div className="w-12 h-12 mb-4 text-orange-400 opacity-80"><svg fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">{stat.icon}</svg></div>
                <div className="text-4xl md:text-5xl font-black text-white mb-2 tracking-tighter">
                  <span className="stat-counter" data-target={stat.metric}>0</span>
                  <span className="text-orange-500">{stat.suffix}</span>
                </div>
                {/* Contrast fix: text-slate-300 -> text-slate-100 */}
                <p className="text-[11px] font-bold text-slate-100 uppercase tracking-widest mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. CALL TO ACTION STRIP */}
      <section className="bg-orange-500 py-20 relative overflow-hidden" aria-label="Final call to action banner for delegate secure access passes" style={{ contentVisibility: 'auto', containIntrinsicSize: '300px' }}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(#fff 1.5px, transparent 1.5px)', backgroundSize: '30px 30px' }}></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 flex flex-col lg:flex-row justify-between items-center gap-10 text-center lg:text-left">
          <div>
            {/* ACCESSIBILITY FIX: Switched to text-[#0a1d37] against bg-orange-500 for perfect WCAG contrast passing */}
            <h2 className="text-4xl md:text-5xl font-black text-[#0a1d37] tracking-tight mb-3">Ready to shape the future?</h2>
            <p className="text-xl text-[#0a1d37]/90 font-medium">Early bird registrations and exhibition spaces are now open.</p>
          </div>
          <div className="flex gap-4">
            <Link to="/delegates" className="py-5 px-10 bg-[#0a1d37] hover:bg-slate-800 text-white font-black rounded-2xl transition-all shadow-2xl text-[15px] uppercase tracking-wider hover:-translate-y-1" aria-label="Register as a Delegate for GRIS 2027 Now">Register Now</Link>
          </div>
        </div>
      </section>

    </div>
  );
}